import React, { useState, useEffect, useMemo } from 'react';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Key, 
  Search,
  Filter,
  RefreshCw,
  Save,
  X,
  Check,
  AlertTriangle,
  Eye,
  EyeOff,
  Copy,
  BarChart3,
  Layers,
  Zap,
  Target,
  TrendingUp,
  Settings,
  ChevronDown,
  ChevronRight,
  Info
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { useActivityLogger } from '../hooks/useActivityLogger';
import { rbacService } from '../../backend/services/rbac';
import { formatDate } from '../../shared/utils';
import type { Role, Permission, RoleWithPermissions } from '../../shared/types/auth';
import type { 
  RoleTemplate, 
  PermissionGroup, 
  RoleAnalytics,
  RoleFilters
} from '../../shared/types/role-management';
import { 
  ROLE_TEMPLATES,
  PERMISSION_GROUPS,
  getRoleCategoryColor,
  getPermissionActionColor,
  getPermissionGroupColor
} from '../../shared/types/role-management';
import { FEATURE_NAMES } from '../../shared/types/user-management';
import { toast } from 'sonner';

// Role Statistics Component with improved design
const RoleStatistics: React.FC<{ analytics: RoleAnalytics | null; loading: boolean }> = ({ analytics, loading }) => {
  const stats = [
    {
      icon: Shield,
      label: 'Total Roles',
      value: analytics?.total_roles || 0,
      color: 'blue',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      borderColor: 'border-blue-200'
    },
    {
      icon: Users,
      label: 'Active Roles',
      value: analytics?.active_roles || 0,
      color: 'green',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      borderColor: 'border-green-200'
    },
    {
      icon: Key,
      label: 'Permission Coverage',
      value: `${analytics?.permission_coverage || 0}%`,
      color: 'purple',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      borderColor: 'border-purple-200'
    },
    {
      icon: TrendingUp,
      label: 'Security Score',
      value: `${analytics?.security_score || 0}/100`,
      color: 'amber',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
      borderColor: 'border-amber-200'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div
            key={index}
            className={`relative overflow-hidden rounded-xl border-2 ${stat.borderColor} ${stat.bgColor} p-6 transition-all hover:shadow-lg hover:scale-105`}
          >
            {loading ? (
              <div className="animate-pulse">
                <div className="h-12 bg-gray-200 rounded mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-2/3"></div>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
                      {stat.label}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.iconColor}`} />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-current to-transparent opacity-20"></div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Role Templates Component
const RoleTemplatesSection: React.FC<{
  templates: RoleTemplate[];
  onCreateFromTemplate: (template: RoleTemplate) => void;
  canManageRoles: boolean;
}> = ({ templates, onCreateFromTemplate, canManageRoles }) => {
  const [showTemplates, setShowTemplates] = useState(false);

  if (!canManageRoles) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Layers className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Role Templates</h3>
        </div>
        <button
          onClick={() => setShowTemplates(!showTemplates)}
          className="flex items-center text-sm text-blue-600 hover:text-blue-800"
        >
          {showTemplates ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
          {showTemplates ? 'Hide' : 'Show'} Templates
        </button>
      </div>

      {showTemplates && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.filter(t => t.is_default).map((template) => (
            <div
              key={template.id}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center">
                  <span className="text-2xl mr-2">{template.icon}</span>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{template.name}</h4>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleCategoryColor(template.category)}`}>
                      {template.category.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-gray-600 mb-3">{template.description}</p>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {template.permissions.length} permissions
                </span>
                <button
                  onClick={() => onCreateFromTemplate(template)}
                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Use Template
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Enhanced Permission Matrix Component
const EnhancedPermissionMatrix: React.FC<{
  role: RoleWithPermissions;
  allPermissions: Permission[];
  permissionGroups: PermissionGroup[];
  onPermissionToggle: (permissionId: string, granted: boolean) => void;
  loading: boolean;
}> = ({ role, allPermissions, permissionGroups, onPermissionToggle, loading }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['artist_management', 'campaign_operations']));
  const [searchTerm, setSearchTerm] = useState('');

  // Group permissions by permission groups
  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, Permission[]> = {};
    
    // Initialize with permission groups
    permissionGroups.forEach(group => {
      grouped[group.id] = [];
    });

    // Add ungrouped category
    grouped['ungrouped'] = [];

    allPermissions.forEach(permission => {
      let assigned = false;
      
      // Check if permission belongs to any group
      for (const group of permissionGroups) {
        if (group.permissions.some(p => permission.name.includes(p) || p.includes(permission.action))) {
          grouped[group.id].push(permission);
          assigned = true;
          break;
        }
      }
      
      // If not assigned to any group, add to ungrouped
      if (!assigned) {
        grouped['ungrouped'].push(permission);
      }
    });

    return grouped;
  }, [allPermissions, permissionGroups]);

  // Filter permissions based on search
  const filteredGroups = useMemo(() => {
    if (!searchTerm) return groupedPermissions;

    const filtered: Record<string, Permission[]> = {};
    Object.entries(groupedPermissions).forEach(([groupId, permissions]) => {
      const filteredPermissions = permissions.filter(permission =>
        permission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        permission.resource.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (filteredPermissions.length > 0) {
        filtered[groupId] = filteredPermissions;
      }
    });
    
    return filtered;
  }, [groupedPermissions, searchTerm]);

  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const getGroupInfo = (groupId: string) => {
    if (groupId === 'ungrouped') {
      return {
        name: 'Other Permissions',
        description: 'Permissions not categorized in specific groups',
        icon: '📁',
        color: 'gray'
      };
    }
    return permissionGroups.find(g => g.id === groupId);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Permissions for {role.name}
        </h3>
        <div className="text-sm text-gray-500">
          {role.permissions.length} of {allPermissions.length} permissions granted
        </div>
      </div>

      {/* Permission Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Permission Groups */}
      <div className="space-y-4">
        {Object.entries(filteredGroups).map(([groupId, permissions]) => {
          const groupInfo = getGroupInfo(groupId);
          if (!groupInfo || permissions.length === 0) return null;

          const isExpanded = expandedGroups.has(groupId);
          const grantedCount = permissions.filter(p => role.permissions.some(rp => rp.id === p.id)).length;

          return (
            <div key={groupId} className="border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleGroup(groupId)}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{groupInfo.icon}</span>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{groupInfo.name}</h4>
                    <p className="text-xs text-gray-500">{groupInfo.description}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500">
                    {grantedCount}/{permissions.length} granted
                  </span>
                  <div className={`w-16 h-2 bg-gray-200 rounded-full overflow-hidden`}>
                    <div 
                      className={`h-full bg-${groupInfo.color}-500 transition-all duration-300`}
                      style={{ width: `${(grantedCount / permissions.length) * 100}%` }}
                    />
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-200 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {permissions.map((permission) => {
                      const isGranted = role.permissions.some(p => p.id === permission.id);
                      return (
                        <label
                          key={permission.id}
                          className={`relative flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                            isGranted 
                              ? 'bg-blue-50 border-blue-200' 
                              : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isGranted}
                            onChange={(e) => onPermissionToggle(permission.id, e.target.checked)}
                            disabled={loading}
                            className="sr-only"
                          />
                          <div className="flex items-center w-full">
                            <div className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center mr-3 ${
                              isGranted 
                                ? 'bg-blue-600 border-blue-600' 
                                : 'border-gray-300'
                            }`}>
                              {isGranted && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium ${getPermissionActionColor(permission.action)} px-2 py-1 rounded-full inline-block mb-1`}>
                                {permission.action}
                              </div>
                              <div className="text-xs text-gray-600 truncate">
                                {permission.resource}
                              </div>
                              {permission.description && (
                                <div className="text-xs text-gray-500 mt-1 truncate">
                                  {permission.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main Enhanced AdminRoles Component
export const EnhancedAdminRoles: React.FC = () => {
  const { hasRole: effectiveHasRole } = useEffectivePermissions();
  const { trackFeature, logRoleManagementAction } = useActivityLogger();
  
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleWithPermissions | null>(null);
  const [analytics, setAnalytics] = useState<RoleAnalytics | null>(null);
  const [templates, setTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [permissionLoading, setPermissionLoading] = useState(false);
  
  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<RoleFilters>({
    search: '',
    category: '',
    permission_group: '',
    user_count_range: { min: 0, max: 1000 },
    activity_level: '',
    security_risk: '',
    template_based: false
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: ''
  });

  // Permission groups from constants
  const permissionGroups = Object.values(PERMISSION_GROUPS).sort((a, b) => a.order - b.order);

  // Check if user can manage roles
  const canManageRoles = effectiveHasRole('admin') || effectiveHasRole('super');

  // Track feature usage
  useEffect(() => {
    trackFeature(FEATURE_NAMES.role_management);
  }, [trackFeature]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const rolesData = await rbacService.getRoles();
      setRoles(rolesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch roles');
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const permissionsData = await rbacService.getPermissions();
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      console.log('Fetching analytics with roles:', roles.length);
      const analyticsData = await rbacService.getRoleAnalytics();
      console.log('Analytics data received:', analyticsData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      // Fallback to basic analytics if the service fails
      const fallbackAnalytics: RoleAnalytics = {
        total_roles: roles.length,
        active_roles: roles.filter(r => r.is_active).length,
        users_by_role: {},
        permission_coverage: 0,
        most_used_permissions: [],
        least_used_permissions: [],
        role_distribution: [],
        security_score: 50,
        recommendations: ['Unable to load detailed analytics']
      };
      setAnalytics(fallbackAnalytics);
    }
  };

  const fetchRoleWithPermissions = async (roleId: string) => {
    try {
      const roleData = await rbacService.getRoleWithPermissions(roleId);
      setSelectedRole(roleData);
    } catch (err) {
      toast.error('Failed to load role details');
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const templatesData = await rbacService.getRoleTemplates();
      setTemplates(templatesData);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  };

  useEffect(() => {
    if (roles.length > 0) {
      fetchAnalytics();
    }
  }, [roles]);

  // Filter roles
  const filteredRoles = useMemo(() => {
    return roles.filter(role =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      role.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [roles, searchTerm]);

  const handleCreateRole = async () => {
    if (!roleForm.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      setLoading(true);
      const newRole = await rbacService.createRole({
        name: roleForm.name.toLowerCase(),
        description: roleForm.description,
        is_active: true
      });
      
      await logRoleManagementAction('create', newRole.id, newRole.name, {
        description: roleForm.description
      });
      
      toast.success('Role created successfully');
      setShowCreateModal(false);
      setRoleForm({ name: '', description: '' });
      await fetchRoles();
    } catch (err) {
      toast.error('Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (template: RoleTemplate) => {
    try {
      setLoading(true);
      const newRole = await rbacService.createRole({
        name: template.name.toLowerCase().replace(/\s+/g, '_'),
        description: template.description,
        is_active: true
      });

      // Assign template permissions
      for (const permissionName of template.permissions) {
        const permission = permissions.find(p => p.name === permissionName);
        if (permission) {
          await rbacService.assignPermissionToRole(newRole.id, permission.id);
        }
      }

      await logRoleManagementAction('create', newRole.id, newRole.name, {
        template_used: template.name,
        permissions_count: template.permissions.length
      });

      toast.success(`Role created from ${template.name} template`);
      await fetchRoles();
    } catch (err) {
      toast.error('Failed to create role from template');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = async (permissionId: string, granted: boolean) => {
    if (!selectedRole) return;

    setPermissionLoading(true);
    try {
      if (granted) {
        await rbacService.assignPermissionToRole(selectedRole.id, permissionId);
      } else {
        await rbacService.removePermissionFromRole(selectedRole.id, permissionId);
      }
      
      await logRoleManagementAction(
        granted ? 'assign' : 'revoke', 
        selectedRole.id, 
        selectedRole.name,
        { permission_id: permissionId, action: granted ? 'granted' : 'revoked' }
      );
      
      // Refresh the selected role
      await fetchRoleWithPermissions(selectedRole.id);
      toast.success(`Permission ${granted ? 'granted' : 'revoked'}`);
    } catch (err) {
      toast.error('Failed to update permission');
    } finally {
      setPermissionLoading(false);
    }
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName) {
      case 'super': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'admin': return 'bg-red-100 text-red-800 border-red-200';
      case 'manager': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'user': return 'bg-green-100 text-green-800 border-green-200';
      case 'client': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading && roles.length === 0) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-24 bg-gray-100 rounded"></div>
                ))}
              </div>
              <div className="h-96 bg-gray-100 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto">
        <PageHeader
          icon={<Shield className="h-8 w-8" />}
          title="Role Management"

          actions={canManageRoles ? (
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Plus className="h-4 w-4 mr-2" /> Create Role
              </button>
              <button
                onClick={fetchRoles}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </button>
            </div>
          ) : null}
        />

        {/* Role Statistics */}
        <RoleStatistics analytics={analytics} loading={loading} />

        {/* Role Templates */}
        <RoleTemplatesSection
          templates={templates}
          onCreateFromTemplate={handleCreateFromTemplate}
          canManageRoles={canManageRoles}
        />

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Roles List */}
          <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Roles</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                {filteredRoles.map((role) => (
                  <div
                    key={role.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedRole?.id === role.id
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                    onClick={() => fetchRoleWithPermissions(role.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(role.name)}`}>
                            {role.name}
                          </span>
                          {!role.is_active && (
                            <span className="text-xs text-gray-500">(Inactive)</span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-600">{role.description}</p>
                        <p className="text-xs text-gray-500">
                          Created {formatDate(role.created_at)}
                        </p>
                      </div>
                      
                      {canManageRoles && (
                        <div className="flex items-center space-x-1 ml-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle edit
                            }}
                            className="p-1 text-gray-400 hover:text-blue-600"
                            title="Edit role"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle clone
                            }}
                            className="p-1 text-gray-400 hover:text-green-600"
                            title="Clone role"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Handle delete
                            }}
                            className="p-1 text-gray-400 hover:text-red-600"
                            title="Delete role"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {filteredRoles.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Shield className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No roles found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating your first role.'}
                  </p>
                  {!searchTerm && canManageRoles && (
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create Your First Role
                    </button>
                  )}
                </div>
              )}

              {loading && roles.length === 0 && (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="p-4 rounded-lg border bg-gray-50 animate-pulse">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="h-6 w-16 bg-gray-200 rounded-full"></div>
                        <div className="h-4 w-20 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-4 w-full bg-gray-200 rounded mb-1"></div>
                      <div className="h-3 w-32 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Role Details & Permissions */}
          <div className="space-y-4">
            {selectedRole ? (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <EnhancedPermissionMatrix
                  role={selectedRole}
                  allPermissions={permissions}
                  permissionGroups={permissionGroups}
                  onPermissionToggle={handlePermissionToggle}
                  loading={permissionLoading}
                />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-center py-12">
                  <Shield className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Select a role</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Choose a role from the list to view and manage its permissions.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Role Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Create New Role</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role Name
                    </label>
                    <input
                      type="text"
                      value={roleForm.name}
                      onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., editor, viewer"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={roleForm.description}
                      onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Describe what this role can do..."
                    />
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateRole}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Role'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedAdminRoles;
